import { useState } from "react";
import { Copy, Share2, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Native share buttons for WhatsApp / Email / Facebook / X — with a "Copy link"
 * fallback. Used on the referral dashboard.
 */
export default function ShareButtons({ url, text, className = "" }) {
  const { user } = useAuth();
  const [copied, setCopied] = useState(false);
  const fullText = `${text}\n\n${url}`;

  const channels = [
    {
      key: "whatsapp",
      label: "WhatsApp",
      href: `https://wa.me/?text=${encodeURIComponent(fullText)}`,
      color: "#25D366",
    },
    {
      key: "facebook",
      label: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
      color: "#1877F2",
    },
    {
      key: "twitter",
      label: "X / Twitter",
      href: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      color: "#000000",
    },
    {
      key: "email",
      label: "Email",
      href: `mailto:?subject=${encodeURIComponent("Une pépite BAYO pour toi")}&body=${encodeURIComponent(fullText)}`,
      color: "var(--ink-2)",
    },
  ];

  const copy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for non-secure contexts / older browsers
        const ta = document.createElement("textarea");
        ta.value = url;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      if (process.env.NODE_ENV !== "production") console.warn("Clipboard copy refused:", e?.message);
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "BAYO", text, url });
      } catch (e) {
        // User cancelled the share sheet — silent on purpose
        if (e?.name !== "AbortError" && process.env.NODE_ENV !== "production") {
          console.warn("Share failed:", e?.message);
        }
      }
    } else {
      copy();
    }
  };

  return (
    <div data-testid="share-buttons" className={`flex flex-wrap gap-3 ${className}`}>
      {channels.map((c) => (
        <a
          key={c.key}
          data-testid={`share-${c.key}`}
          href={c.href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-[color:rgba(0,16,48,0.18)] px-4 py-2 text-xs tracking-[0.18em] uppercase font-medium hover:border-[color:var(--ink)] transition-colors"
          style={{ color: c.color }}
        >
          {c.label}
        </a>
      ))}
      <button
        data-testid="share-copy"
        onClick={copy}
        className="inline-flex items-center gap-2 border border-[color:rgba(0,16,48,0.18)] px-4 py-2 text-xs tracking-[0.18em] uppercase font-medium hover:border-[color:var(--ink)]"
      >
        {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copié" : "Copier le lien"}
      </button>
      {typeof navigator !== "undefined" && navigator.share && user && (
        <button
          data-testid="share-native"
          onClick={nativeShare}
          className="inline-flex items-center gap-2 border border-[color:rgba(0,16,48,0.18)] px-4 py-2 text-xs tracking-[0.18em] uppercase font-medium hover:border-[color:var(--ink)]"
        >
          <Share2 size={14} /> Partager
        </button>
      )}
    </div>
  );
}
